import { ReactNode, useEffect, useState } from "react";
import {
  Newspaper,
  Code,
  Brain,
  Cloud,
  Palette,
  Shield,
  Sparkles,
  ArrowRight,
  TrendingUp,
  Star,
  Clock,
} from "lucide-react";
import { getAllPosts, getCategoriesFromPosts, mergePosts, BlogPost, BlogCategory } from "@/data/blogData";
import { fetchGeneratedPosts } from "@/services/blogService";
import {
  getBlogHomeUrl,
  getBlogPostUrl,
  getBlogCategoryUrl,
} from "@/lib/blogUrls";
import { useSiteLanguage } from "@/lib/useSiteLanguage";
import { blogNavPopupCopy } from "@/lib/navPopupCopy";

interface BlogNavigationPopupProps {
  isVisible: boolean;
  activeItem: string | null;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  closePopup?: () => void;
}

// Category icons mapping
const categoryIcons: Record<string, ReactNode> = {
  technology: <Sparkles className="w-5 h-5 text-foreground" />,
  development: <Code className="w-5 h-5 text-foreground" />,
  design: <Palette className="w-5 h-5 text-foreground" />,
  infrastructure: <Cloud className="w-5 h-5 text-foreground" />,
  security: <Shield className="w-5 h-5 text-foreground" />,
  events: <TrendingUp className="w-5 h-5 text-foreground" />,
  ai: <Brain className="w-5 h-5 text-foreground" />,
};

export function BlogNavigationPopup({
  isVisible,
  activeItem,
  onMouseEnter,
  onMouseLeave,
  closePopup,
}: BlogNavigationPopupProps) {
  const lang = useSiteLanguage();
  const t = blogNavPopupCopy[lang];
  const [featuredPosts, setFeaturedPosts] = useState<BlogPost[]>(() =>
    getAllPosts().filter((p) => p.featured).slice(0, 3)
  );
  const [categories, setCategories] = useState<BlogCategory[]>(() =>
    getCategoriesFromPosts(getAllPosts())
  );

  useEffect(() => {
    let cancelled = false;
    void fetchGeneratedPosts().then((generated) => {
      if (cancelled) return;
      const merged = mergePosts(getAllPosts(), generated);
      setFeaturedPosts(merged.filter((p) => p.featured).slice(0, 3));
      setCategories(getCategoriesFromPosts(merged));
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (activeItem !== "blog") {
    return null;
  }

  const handleNavigation = (href: string) => {
    if (typeof closePopup === "function") closePopup();
    window.location.href = href;
  };

  return (
    <div
      className={`fixed top-14 left-0 z-50 w-full rounded-b-2xl bg-background border-b border-border shadow-2xl overflow-hidden transition-all duration-300 ease-out transform ${
        isVisible
          ? "opacity-100 translate-y-0 pointer-events-auto"
          : "opacity-0 -translate-y-3 pointer-events-none"
      }`}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="px-6 lg:px-12">
        <div className="py-8">
          <div className="grid grid-cols-12 gap-6">
            {/* Left content column: hero + featured posts */}
            <div className="col-span-12 lg:col-span-5 pr-6 border-r border-border">
              <div className="mb-6">
                <h3 className="text-lg font-bold text-foreground mb-2">
                  {t.title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {t.description}
                </p>
              </div>

              {/* Featured Posts */}
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                  <Star className="w-3 h-3" />
                  {t.featured}
                </div>
                {featuredPosts.map((post) => (
                  <button
                    key={post.id}
                    onClick={() => handleNavigation(getBlogPostUrl(post.slug))}
                    className="w-full flex items-start gap-3 p-3 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors group text-left"
                  >
                    <img
                      src={post.coverImage}
                      alt={post.title}
                      className="w-16 h-12 rounded-md object-cover flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-medium text-foreground group-hover:text-cyan-600 dark:group-hover:text-cyan-400 transition-colors line-clamp-1">
                        {post.title}
                      </h4>
                      <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                        <Clock className="w-3 h-3" />
                        <span>
                          {post.readTime} {t.minRead}
                        </span>
                        <span>•</span>
                        <span>{post.category}</span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>

              {/* CTA Button */}
              <button
                onClick={() => handleNavigation(getBlogHomeUrl())}
                className="mt-6 w-full flex items-center justify-center gap-2 px-4 py-3 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-lg font-medium hover:bg-slate-800 dark:hover:bg-slate-100 transition-colors"
              >
                {t.viewAll}
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>

            {/* Right content: categories and trending */}
            <div className="col-span-12 lg:col-span-7">
              <div className="grid grid-cols-2 gap-8">
                {/* Categories Section */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    <Newspaper className="w-3 h-3" />
                    {t.categories}
                  </div>
                  <div className="space-y-1">
                    {categories.map((category) => (
                      <button
                        key={category.id}
                        onClick={() =>
                          handleNavigation(getBlogCategoryUrl(category.slug))
                        }
                        className="w-full text-left p-2.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors group"
                      >
                        <div className="flex items-center gap-3">
                          <div className="flex-shrink-0 text-muted-foreground group-hover:text-foreground transition-colors">
                            {categoryIcons[category.slug] || (
                              <Newspaper className="w-5 h-5 text-foreground" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <h4 className="text-sm font-medium text-foreground group-hover:text-cyan-600 dark:group-hover:text-cyan-400 transition-colors">
                                {category.name}
                              </h4>
                              <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                                {category.count}
                              </span>
                            </div>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {category.description}
                            </p>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Quick Links Section */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    <TrendingUp className="w-3 h-3" />
                    {t.quickLinks}
                  </div>
                  <div className="space-y-1">
                    <button
                      onClick={() => handleNavigation(getBlogHomeUrl())}
                      className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors group"
                    >
                      <div className="flex items-center gap-3">
                        <Newspaper className="w-5 h-5 text-muted-foreground group-hover:text-foreground transition-colors" />
                        <span className="text-sm font-medium text-foreground">
                          {t.quickLinkLabels.allArticles}
                        </span>
                      </div>
                      <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:translate-x-1 transition-transform" />
                    </button>
                    <button
                      onClick={() =>
                        handleNavigation(getBlogCategoryUrl("technology"))
                      }
                      className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors group"
                    >
                      <div className="flex items-center gap-3">
                        <Sparkles className="w-5 h-5 text-muted-foreground group-hover:text-foreground transition-colors" />
                        <span className="text-sm font-medium text-foreground">
                          {t.quickLinkLabels.latestTech}
                        </span>
                      </div>
                      <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:translate-x-1 transition-transform" />
                    </button>
                    <button
                      onClick={() =>
                        handleNavigation(getBlogCategoryUrl("development"))
                      }
                      className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors group"
                    >
                      <div className="flex items-center gap-3">
                        <Code className="w-5 h-5 text-muted-foreground group-hover:text-foreground transition-colors" />
                        <span className="text-sm font-medium text-foreground">
                          {t.quickLinkLabels.developmentGuides}
                        </span>
                      </div>
                      <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:translate-x-1 transition-transform" />
                    </button>
                    <button
                      onClick={() =>
                        handleNavigation(getBlogCategoryUrl("design"))
                      }
                      className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors group"
                    >
                      <div className="flex items-center gap-3">
                        <Palette className="w-5 h-5 text-muted-foreground group-hover:text-foreground transition-colors" />
                        <span className="text-sm font-medium text-foreground">
                          {t.quickLinkLabels.designInsights}
                        </span>
                      </div>
                      <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:translate-x-1 transition-transform" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
